import useToken from 'hooks/useToken';
import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Row, Spinner } from 'react-bootstrap';
import { FiCalendar, FiUser } from 'react-icons/fi';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';
import TeamVacationsCalendar from '../../profile/TeamVacationsCalendar';
import VacationDetailsModal from '../../profile/VacationDetailsModal';

const VacacionesPendientes = () => {
    const { token } = useToken();
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [imagenesPerfil, setImagenesPerfil] = useState({});
    const [cargandoImagenes, setCargandoImagenes] = useState({});

    /**
     * Obtener el color del badge según el estado
     */
    const getStatusBadge = (estado) => {
        const estados = {
            'PENDIENTE': { bg: 'warning', text: 'Pendiente' },
            'APROBADA': { bg: 'success', text: 'Aprobada' },
            'RECHAZADA': { bg: 'danger', text: 'Rechazada' }
        };

        const status = estados[estado?.toUpperCase()] || { bg: 'secondary', text: estado };
        return status;
    };

    /**
     * Cargar imagen del perfil del empleado
     */
    const cargarImagenPerfil = async (idUsuario) => {
        if (!idUsuario) return;

        setCargandoImagenes(prev => ({ ...prev, [idUsuario]: true }));

        try {
            const response = await fetch(`/api/perfil/obtenerImagenPerfil/?idUsuario=${idUsuario}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'text/plain'
                }
            });

            if (response.ok) {
                const data = await response.text();
                setImagenesPerfil(prev => ({ ...prev, [idUsuario]: data }));
            } else {
                console.error('Error al cargar imagen:', response.status);
                setImagenesPerfil(prev => ({ ...prev, [idUsuario]: '' }));
            }
        } catch (error) {
            console.error('Error al cargar imagen del perfil:', error);
            setImagenesPerfil(prev => ({ ...prev, [idUsuario]: '' }));
        } finally {
            setCargandoImagenes(prev => ({ ...prev, [idUsuario]: false }));
        }
    };

    // Skeleton Loading
    const SolicitudSkeleton = () => (
        <Row className="g-2 p-2">
            {[1, 2, 3, 4].map((i) => (
                <Col key={i} xs={12}>
                    <div style={{
                        padding: "12px",
                        border: "1px solid #e3e3e3",
                        borderRadius: "8px",
                        background: "transparent"
                    }}>
                        <Skeleton height={20} width="70%" className="mb-2" />
                        <Skeleton height={16} width="100%" className="mb-2" />
                        <Skeleton height={16} width="90%" className="mb-2" />
                        <Skeleton height={14} width="40%" />
                    </div>
                </Col>
            ))}
        </Row>
    );

    useEffect(() => {
        const fetchSolicitudes = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch('/api/vacaciones/ObtenerVacacionesPendientes', {
                    headers: {
                        'Authorization': 'Bearer ' + token
                    }
                });
                if (!response.ok) throw new Error('Error al obtener solicitudes');
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    setSolicitudes(parsed.table1 || []);

                    // Cargar imágenes de perfil para empleados únicos
                    const uniqueIds = [...new Set((parsed.table1 || []).map(s => s.idUsuario))];
                    uniqueIds.forEach(id => {
                        if (!imagenesPerfil[id] && !cargandoImagenes[id]) {
                            cargarImagenPerfil(id);
                        }
                    });
                } else {
                    setSolicitudes([]);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchSolicitudes();
    }, [token]);

    return (
        <Card>
            <Card.Header className="bg-light border-bottom px-4 py-3">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <i className="fas fa-calendar-alt me-2 text-primary"></i>
                        <h5 className="mb-0 fw-bold text-800">Vacaciones Pendientes</h5>
                        {solicitudes.length > 0 && (
                            <span className="badge bg-primary bg-opacity-10 text-primary ms-2 rounded-pill py-1 px-2 small">
                                {solicitudes.length}
                            </span>
                        )}
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="p-2 overflow-x-hidden" id="scrollableTargetVacaciones" style={{ maxHeight: '600px', overflow: 'auto' }}>
                {loading ? (
                    <SolicitudSkeleton />
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : (
                    solicitudes.length === 0 ? (
                        <div className="text-center py-5">
                            <h6 className="text-muted mb-2">Sin solicitudes</h6>
                            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                No hay solicitudes de vacaciones pendientes
                            </p>
                        </div>
                    ) : (
                        <Row className="g-2">
                            {solicitudes.map((solicitud, index) => {
                                return (
                                    <Col key={`${solicitud.idVacacion}-${index}`} xs={12}>
                                        <div style={{
                                            padding: "16px 20px",
                                            border: "1px solid #e3e3e3",
                                            borderRadius: "12px",
                                            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                                            transition: "all 0.3s ease",
                                            cursor: "pointer",
                                            position: "relative",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                            borderLeft: `4px solid ${getStatusBadge(solicitud.estado).bg === 'warning' ? '#ffc107' :
                                                getStatusBadge(solicitud.estado).bg === 'success' ? '#198754' :
                                                    getStatusBadge(solicitud.estado).bg === 'danger' ? '#dc3545' : '#6c757d'}`
                                        }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateY(-2px)";
                                                e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
                                                e.currentTarget.style.borderColor = "#0d6efd";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)";
                                                e.currentTarget.style.borderColor = "#e3e3e3";
                                            }}
                                            onClick={() => {
                                                setSelectedEmployee(solicitud);
                                                setShowModal(true);
                                            }}>
                                            {/* Estado en esquina superior derecha */}
                                            <span style={{
                                                position: "absolute",
                                                top: "12px",
                                                right: "16px",
                                                fontSize: "0.7rem",
                                                padding: "2px 6px",
                                                background: getStatusBadge(solicitud.estado).bg === 'warning' ? '#ffc107' :
                                                    getStatusBadge(solicitud.estado).bg === 'success' ? '#198754' :
                                                        getStatusBadge(solicitud.estado).bg === 'danger' ? '#dc3545' : '#6c757d',
                                                borderRadius: "4px",
                                                color: getStatusBadge(solicitud.estado).bg === 'warning' ? '#000' : '#fff'
                                            }}>
                                                {getStatusBadge(solicitud.estado).text}
                                            </span>

                                            {/* Primera línea: Empleado */}
                                            <div className="mb-3 d-flex align-items-center" style={{ paddingRight: "60px" }}>
                                                {cargandoImagenes[solicitud.idUsuario] ? (
                                                    <div style={{ width: '48px', height: '48px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
                                                        <Spinner animation="border" size="sm" variant="primary" />
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={imagenesPerfil[solicitud.idUsuario] || '/img/team/nofoto50.png'}
                                                        alt={solicitud.nombreEmpleado}
                                                        style={{
                                                            width: '48px',
                                                            height: '48px',
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            border: '2px solid #e9ecef',
                                                            marginRight: '12px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = "/img/team/nofoto50.png";
                                                        }}
                                                    />
                                                )}
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: "1rem", marginBottom: "4px" }}>
                                                        <FiUser size={16} className="text-primary me-2" />
                                                        <Link
                                                            to={`/perfil/${solicitud.idUsuario}`}
                                                            className="text-decoration-none text-dark"
                                                            style={{ fontSize: "1rem" }}
                                                        >
                                                            {solicitud.nombreEmpleado}
                                                        </Link>
                                                    </h6>
                                                    <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                        Solicitud #{solicitud.idVacacion}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Rango de fechas */}
                                            <div className="mb-2">
                                                <FiCalendar size={14} className="text-info me-2" />
                                                <span className="fw-semibold text-secondary" style={{ fontSize: "0.85rem" }}>Periodo solicitado: </span>
                                                <span className="text-dark fw-medium" style={{ fontSize: "0.85rem" }}>
                                                    {new Date(solicitud.dFechaInicio).toLocaleDateString('es-ES', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })} - {new Date(solicitud.dFechaFin).toLocaleDateString('es-ES', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </span>
                                            </div>

                                            {/* Días específicos si existen */}
                                            {solicitud.vFechasJson && (() => {
                                                try {
                                                    const fechas = JSON.parse(solicitud.vFechasJson);
                                                    if (Array.isArray(fechas) && fechas.length > 0) {
                                                        return (
                                                            <div className="mt-2 p-2 bg-light rounded" style={{ border: '1px solid #e9ecef' }}>
                                                                <FiCalendar size={14} className="text-success me-2" />
                                                                <span className="fw-semibold text-secondary" style={{ fontSize: "0.8rem" }}>
                                                                    Días específicos ({fechas.length}):
                                                                </span>
                                                                <div className="mt-1">
                                                                    <small className="text-muted" style={{ lineHeight: '1.4' }}>
                                                                        {fechas.map((fecha, idx) => (
                                                                            <span key={idx} className="badge bg-light text-dark me-1 mb-1" style={{ fontSize: '0.7rem' }}>
                                                                                {new Date(fecha + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                                                            </span>
                                                                        ))}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                } catch (e) {
                                                    return null;
                                                }
                                            })()}
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    )
                )}
            </Card.Body>
            <Card.Footer>
                <div className="text-center text-muted">
                    {solicitudes.length} solicitud{solicitudes.length !== 1 ? 'es' : ''} pendiente{solicitudes.length !== 1 ? 's' : ''}
                </div>
            </Card.Footer>
            <TeamVacationsCalendar />
            <VacationDetailsModal
                show={showModal}
                onHide={() => setShowModal(false)}
                employee={selectedEmployee}
                onSuccess={() => {
                    // Refresh the list
                    const fetchSolicitudes = async () => {
                        setLoading(true);
                        setError(null);
                        try {
                            const response = await fetch('/api/vacaciones/ObtenerVacacionesPendientes', {
                                headers: {
                                    'Authorization': 'Bearer ' + token
                                }
                            });
                            if (!response.ok) throw new Error('Error al obtener solicitudes');
                            const data = await response.json();
                            if (data.iResponseType === 1) {
                                const parsed = JSON.parse(data.detalles);
                                setSolicitudes(parsed.table1 || []);

                                // Cargar imágenes de perfil para empleados únicos
                                const uniqueIds = [...new Set((parsed.table1 || []).map(s => s.idUsuario))];
                                uniqueIds.forEach(id => {
                                    if (!imagenesPerfil[id] && !cargandoImagenes[id]) {
                                        cargarImagenPerfil(id);
                                    }
                                });
                            } else {
                                setSolicitudes([]);
                            }
                        } catch (err) {
                            setError(err.message);
                        } finally {
                            setLoading(false);
                        }
                    };
                    fetchSolicitudes();
                }}
            />
        </Card>
    );
};

export default VacacionesPendientes;
